import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR, DIVISIONS, fmtDate } from "@/lib/constants";

const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const DAY_ABBR = ["Sen","Sel","Rab","Kam","Jum","Sab","Min"]; // Monday-first
const LEAVE_LABEL = { cuti: "Cuti", izin: "Izin", sakit: "Sakit" };
const LEAVE_TYPE_CLS = {
  cuti: "bg-sky-100 text-sky-800 border-sky-300",
  izin: "bg-violet-100 text-violet-800 border-violet-300",
  sakit: "bg-rose-100 text-rose-800 border-rose-300",
};

function pad(n) { return String(n).padStart(2, "0"); }
function isoDate(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }

export default function LeaveCalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [division, setDivision] = useState("all");
  const [data, setData] = useState({ leaves: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (division && division !== "all") params.division = division;
      const { data } = await api.get("/leave-requests/calendar", { params });
      setData(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [year, month, division]);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  // Build 6-row grid, Monday-first
  const grid = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    let firstDow = first.getDay(); // 0=Sun
    firstDow = (firstDow + 6) % 7; // shift so Monday=0
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const leavesByDay = useMemo(() => {
    const map = {};
    for (const lv of data.leaves || []) {
      const s = new Date(lv.start_date + "T00:00:00");
      const e = new Date(lv.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() + 1 === month) {
          const key = d.getDate();
          if (!map[key]) map[key] = [];
          map[key].push(lv);
        }
      }
    }
    return map;
  }, [data.leaves, year, month]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-slate-500">Requests</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] flex items-center gap-2">
            <CalendarDays size={24} className="text-[#FF5A1F]" /> Kalender Cuti
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 text-xs">
            <Filter size={13} className="text-slate-500" />
            <select
              data-testid="cal-div-filter"
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              className="h-9 border border-slate-300 bg-white px-2 text-xs hover:border-[#0B1D3A]"
            >
              <option value="all">Semua Divisi</option>
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <button data-testid="btn-cal-prev" onClick={() => shiftMonth(-1)} className="h-9 w-9 border border-slate-300 bg-white hover:border-[#0B1D3A] inline-flex items-center justify-center"><ChevronLeft size={14} /></button>
          <div className="text-sm font-semibold text-[#0B1D3A] tabular-nums min-w-[160px] text-center" data-testid="cal-title">{MONTHS_ID[month - 1]} {year}</div>
          <button data-testid="btn-cal-next" onClick={() => shiftMonth(1)} className="h-9 w-9 border border-slate-300 bg-white hover:border-[#0B1D3A] inline-flex items-center justify-center"><ChevronRight size={14} /></button>
          <button data-testid="btn-cal-today" onClick={() => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth() + 1); }} className="h-9 px-3 border border-slate-300 bg-white hover:border-[#0B1D3A] text-xs uppercase tracking-wider font-semibold">Today</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {DAY_ABBR.map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const dayLeaves = d ? (leavesByDay[d] || []) : [];
            const isToday = d && new Date().toDateString() === new Date(year, month - 1, d).toDateString();
            return (
              <div
                key={d ? isoDate(year, month, d) : `pad-${year}-${month}-${i}`}
                data-testid={d ? `cal-day-${isoDate(year, month, d)}` : undefined}
                className={`min-h-[112px] border-b border-r border-slate-100 p-1.5 ${d ? "bg-white" : "bg-slate-50"}`}
              >
                {d && (
                  <>
                    <div className={`text-[11px] font-semibold tabular-nums mb-1 ${isToday ? "text-[#FF5A1F]" : "text-slate-600"}`}>
                      {d}{isToday && <span className="ml-1 text-[9px] uppercase tracking-wider text-[#FF5A1F]">today</span>}
                    </div>
                    <div className="space-y-1">
                      {dayLeaves.slice(0, 4).map((lv) => (
                        <div
                          key={lv.id + "_" + d}
                          className={`text-[9px] px-1.5 py-0.5 border truncate ${LEAVE_TYPE_CLS[lv.leave_type] || "bg-slate-100 text-slate-700"}`}
                          title={`${lv.user_name} · ${LEAVE_LABEL[lv.leave_type]} · ${fmtDate(lv.start_date)} → ${fmtDate(lv.end_date)}`}
                        >
                          <span className="font-semibold uppercase tracking-wider">{LEAVE_LABEL[lv.leave_type]?.[0] || "?"}</span> {lv.user_name.split(" ")[0]}
                        </div>
                      ))}
                      {dayLeaves.length > 4 && (
                        <div className="text-[9px] text-slate-500 pl-1">+{dayLeaves.length - 4} lagi</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-xs text-slate-400 text-center">Memuat…</div>}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 text-[10px]">
        <span className="text-slate-500 uppercase tracking-wider">Legenda:</span>
        {["cuti", "izin", "sakit"].map((t) => (
          <span key={t} className={`px-2 py-0.5 border ${LEAVE_TYPE_CLS[t]}`}>{LEAVE_LABEL[t]}</span>
        ))}
      </div>
    </div>
  );
}
